import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import type { ReactNode } from "react";
import { useUsageRun } from "../hooks/useUsageTracking";
import type { UsageRun, UsageRunStep } from "../types";
import {
  formatCost,
  formatCount,
  formatSeconds,
  formatTime,
  runTypeLabel,
  stepLabel,
} from "../utils/format";
import { RunStatus } from "./RunStatus";
import { TABLE, TABLE_WRAPPER, TBODY, TD, TD_NUMBER, TH, TH_NUMBER, THEAD } from "./tableClasses";

const RunFacts = ({ run }: { run: UsageRun }) => {
  const facts: Array<[string, ReactNode]> = [
    ["User", run.user_email ?? run.user_id],
    ["Status", <RunStatus key="status" run={run} />],
    ["Model", run.model ?? "—"],
    ["Agent", run.agent ?? "—"],
    ["Command", run.command ?? "—"],
    ["Started", formatTime(run.started_at)],
    ["Agent time", formatSeconds(run.agent_seconds)],
    ["Total time", formatSeconds(run.total_seconds)],
    ["Cost", formatCost(run.cost_usd)],
    ["Calls", run.calls.toLocaleString()],
    ["Input tokens", formatCount(run.input_tokens)],
    ["Cached input tokens", formatCount(run.cache_read_tokens)],
    ["Cache write tokens", formatCount(run.cache_write_tokens)],
    ["Output tokens", formatCount(run.output_tokens)],
    ["Entries without a price", run.unpriced_events.toLocaleString()],
    ["Run ID", run.id],
  ];
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {facts.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs font-medium text-zinc-500">{label}</dt>
          <dd className="mt-0.5 break-words text-zinc-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

const StepRows = ({ steps }: { steps: UsageRunStep[] }) => (
  <div className={TABLE_WRAPPER}>
    <table className={TABLE}>
      <thead className={THEAD}>
        <tr>
          <th className={TH}>Step</th>
          <th className={TH}>Model</th>
          <th className={TH_NUMBER}>Calls</th>
          <th className={TH_NUMBER}>Cost</th>
          <th className={TH_NUMBER}>Input</th>
          <th className={TH_NUMBER}>Cached input</th>
          <th className={TH_NUMBER}>Output</th>
          <th className={TH_NUMBER}>Model time</th>
        </tr>
      </thead>
      <tbody className={TBODY}>
        {steps.map((step) => (
          <tr key={`${step.step}-${step.provider ?? ""}-${step.model ?? ""}`}>
            <td className={`${TD} whitespace-nowrap font-medium text-zinc-900`}>
              {stepLabel(step.step)}
            </td>
            <td className={`${TD} break-all font-mono text-xs`}>{step.model ?? "—"}</td>
            <td className={TD_NUMBER}>{step.calls.toLocaleString()}</td>
            <td className={TD_NUMBER}>
              {step.unpriced_events > 0 && step.cost_usd === 0 ? "No price" : formatCost(step.cost_usd)}
            </td>
            <td className={TD_NUMBER}>{formatCount(step.input_tokens)}</td>
            <td className={TD_NUMBER}>{formatCount(step.cache_read_tokens)}</td>
            <td className={TD_NUMBER}>{formatCount(step.output_tokens)}</td>
            <td className={TD_NUMBER}>
              {step.duration_ms === null ? "—" : formatSeconds(step.duration_ms / 1000)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Keeps the last run while the dialog closes, so its content does not change. */
export const RunDetailDialog = ({
  runId,
  open,
  onClose,
}: {
  runId: string | null;
  open: boolean;
  onClose: () => void;
}) => {
  const detail = useUsageRun(runId);
  const run = detail.data?.run;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Run details</DialogTitle>
          <DialogDescription>
            {run
              ? `${runTypeLabel(run.run_type, run.mode)}, started ${formatTime(run.started_at)}`
              : "The cost and the time of one run, for each step and model."}
          </DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <p className="mt-4 text-sm text-zinc-500" aria-live="polite">
            Loading the run…
          </p>
        ) : detail.isError || !detail.data ? (
          <p role="alert" className="mt-4 text-sm text-zinc-600">
            Could not load the run. Close this window and try again.
          </p>
        ) : (
          <div className="mt-4 space-y-6">
            <RunFacts run={detail.data.run} />
            <section aria-labelledby="usage-run-steps" className="space-y-2">
              <h3 id="usage-run-steps" className="text-sm font-semibold text-zinc-900">
                Steps and models
              </h3>
              {detail.data.steps.length === 0 ? (
                <p className="text-sm text-zinc-500">This run has no recorded model calls.</p>
              ) : (
                <StepRows steps={detail.data.steps} />
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
